import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Select,
  Skeleton,
} from "antd";
import { getReleaseNotesReq } from "./requests";
import "./whats-new.css";

const CHANGE_FILTERS = ["all", "new", "improved", "fixed", "security"];
const RELEASE_TABS = ["all", "new", "major", "improved", "feature"];
const CUSTOMER_AUDIENCE = "customer";

const RELEASE_TAB_LABELS = {
  all: "All",
  new: "New",
  major: "Major",
  improved: "Improved",
  feature: "Feature",
};

const CHANGE_META = {
  new: {
    label: "New",
    className: "whats-new__change-type--new",
  },
  improved: {
    label: "Improved",
    className: "whats-new__change-type--improved",
  },
  fixed: {
    label: "Fixed",
    className: "whats-new__change-type--fixed",
  },
  security: {
    label: "Security",
    className: "whats-new__change-type--security",
  },
};

const isTruthy = (value) => value === true || value === 1 || value === "1" || value === "true";

const titleCase = (value = "") => (
  String(value)
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
);

const slugify = (value = "") => (
  String(value)
    .replace(/[_\s]+/g, "-")
    .replace(/[^a-zA-Z0-9-]/g, "")
    .toLowerCase()
);

const releaseDateValue = (value) => {
  if (!value) return null;

  const date = new Date(String(value).slice(0, 10));
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = releaseDateValue(value);

  if (!date) return "Release date pending";

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
};

const normalizePayload = (response) => {
  if (Array.isArray(response)) return response;

  if (Array.isArray(response?.data?.release_notes)) {
    return response.data.release_notes;
  }

  if (Array.isArray(response?.release_notes)) {
    return response.release_notes;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

const isCustomerSafeRelease = (note) => {
  const date = releaseDateValue(note?.release_date);
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  return note?.status === "published"
    && isTruthy(note?.show_in_changelog)
    && !isTruthy(note?.internal_only)
    && date
    && date <= today;
};

const customerChanges = (changes = []) => (
  (Array.isArray(changes) ? changes : [])
    .filter((change) => !change?.audience || change.audience === CUSTOMER_AUDIENCE)
);

const normalizeReleaseNotes = (response) => (
  normalizePayload(response)
    .filter(isCustomerSafeRelease)
    .map((note) => ({
      ...note,
      changes: customerChanges(note.changes),
    }))
    .filter((note) => note.changes.length > 0)
);

const ensureSuccessfulResponse = (response) => {
  if (!response || response?.response || response?.code === "ERR_NETWORK" || response?.success === false) {
    throw new Error(response?.response?.data?.message || response?.message || "Release notes could not be loaded.");
  }

  return response;
};

const groupedChanges = (changes = []) => (
  changes.reduce((groups, change) => {
    const normalizedType = slugify(change.type);
    const type = CHANGE_META[normalizedType] ? normalizedType : "improved";
    return {
      ...groups,
      [type]: [...(groups[type] || []), change],
    };
  }, {})
);

const releaseMatchesTab = (note, activeTab) => {
  if (activeTab === "all") return true;
  if (["major", "feature"].includes(activeTab)) {
    return slugify(note.release_type) === activeTab;
  }

  return true;
};

const changeMatchesTab = (change, activeTab) => {
  if (activeTab === "all") return true;
  if (["new", "improved"].includes(activeTab)) return slugify(change.type) === activeTab;

  return true;
};

const filterReleaseNotes = (releaseNotes, activeTab, activeModule) => (
  releaseNotes
    .filter((note) => releaseMatchesTab(note, activeTab))
    .map((note) => ({
      ...note,
      changes: note.changes.filter((change) => {
        const matchesType = changeMatchesTab(change, activeTab);
        const matchesModule = activeModule === "all" || change.module === activeModule;

        return matchesType && matchesModule;
      }),
    }))
    .filter((note) => note.changes.length > 0)
);

const moduleOptionsFrom = (releaseNotes) => {
  const modules = new Set();

  releaseNotes.forEach((note) => {
    note.changes.forEach((change) => {
      if (change.module) modules.add(change.module);
    });
  });

  return Array.from(modules).sort();
};

const LoadingState = () => (
  <div className="whats-new__list" aria-label="Loading release notes">
    {[0, 1, 2].map((item) => (
      <div className="whats-new__release" key={ item }>
        <aside className="whats-new__release-meta">
          <Skeleton.Input active size="small" />
          <Skeleton.Input active size="small" />
        </aside>
        <div className="whats-new__marker" aria-hidden="true" />
        <div className="whats-new__release-content">
          <Skeleton active paragraph={ { rows: 4 } } />
        </div>
      </div>
    ))}
  </div>
);

const ReleaseCard = ({ release }) => {
  const groups = groupedChanges(release.changes);
  const releaseType = slugify(release.release_type || "release");

  return (
    <article className={ `whats-new__release whats-new__release--${releaseType}` }>
      <aside className="whats-new__release-meta">
        <span className="whats-new__version">v{ release.version }</span>
        <span className="whats-new__date">{ formatDate(release.release_date) }</span>
        <span className={ `whats-new__release-type whats-new__release-type--${releaseType}` }>
          { titleCase(release.release_type || "release") }
        </span>
      </aside>

      <div className="whats-new__marker" aria-hidden="true" />

      <div className="whats-new__release-content">
        <h2>{ release.headline || `Release ${release.version}` }</h2>
        { release.summary ? <p>{ release.summary }</p> : null }

        <div className="whats-new__changes" aria-label={ `Changes in version ${release.version}` }>
          { CHANGE_FILTERS.filter((type) => type !== "all").map((type) => {
            const changes = groups[type] || [];
            const meta = CHANGE_META[type];

            if (!changes.length) return null;

            return (
              <section className="whats-new__change-group" key={ type }>
                <ul>
                  { changes.map((change, index) => (
                    <li key={ change.id || `${type}-${index}` }>
                      <span className={ `whats-new__change-type ${meta.className}` }>
                        { meta.label }
                      </span>
                      <span className="whats-new__change-copy">{ change.text }</span>
                      { change.module ? <span className="whats-new__module-chip">{ titleCase(change.module) }</span> : null }
                    </li>
                  )) }
                </ul>
              </section>
            );
          }) }
        </div>
      </div>
    </article>
  );
};

const WhatsNewPage = () => {
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [activeModule, setActiveModule] = useState("all");

  const loadReleaseNotes = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getReleaseNotesReq({});
      const notes = normalizeReleaseNotes(ensureSuccessfulResponse(response));
      setReleaseNotes(notes);
    } catch (requestError) {
      setError(requestError?.message || "Release notes could not be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReleaseNotes();
  }, [loadReleaseNotes]);

  const moduleOptions = useMemo(() => moduleOptionsFrom(releaseNotes), [releaseNotes]);
  const filteredReleaseNotes = useMemo(
    () => filterReleaseNotes(releaseNotes, activeTab, activeModule),
    [releaseNotes, activeTab, activeModule]
  );

  useEffect(() => {
    if (moduleOptions.length <= 1 && activeModule !== "all") {
      setActiveModule("all");
    }
  }, [activeModule, moduleOptions.length]);

  return (
    <main className="whats-new">
      <section className="whats-new__hero">
        <div>
          <h1>What's New</h1>
          <p>Everything new, improved and fixed in KLOK</p>
        </div>
      </section>

      <section className="whats-new__toolbar" aria-label="Release note filters">
        <div className="whats-new__filter-pills" role="tablist" aria-label="Filter release notes">
          { RELEASE_TABS.map((tab) => (
            <button
              type="button"
              className={ `whats-new__filter-pill whats-new__filter-pill--${tab}${activeTab === tab ? " whats-new__filter-pill--active" : ""}` }
              onClick={ () => setActiveTab(tab) }
              role="tab"
              aria-selected={ activeTab === tab }
              key={ tab }
            >
              { RELEASE_TAB_LABELS[tab] }
            </button>
          )) }
        </div>

        <Select
          className="whats-new__module-filter"
          value={ activeModule }
          onChange={ setActiveModule }
          options={[
            { value: "all", label: "All modules" },
            ...moduleOptions.map((module) => ({
              value: module,
              label: titleCase(module),
            })),
          ]}
          aria-label="Filter by module"
        />
      </section>

      { error ? (
        <Alert
          className="whats-new__alert"
          type="error"
          showIcon
          message="Release notes could not be loaded"
          description="Check your connection and try again."
          action={ (
            <Button size="small" onClick={ loadReleaseNotes }>
              Retry
            </Button>
          ) }
        />
      ) : null }

      { loading ? <LoadingState /> : null }

      { !loading && !error && filteredReleaseNotes.length === 0 ? (
        <div className="whats-new__empty">
          <Empty
            image={ Empty.PRESENTED_IMAGE_SIMPLE }
            description="No published updates yet."
          />
        </div>
      ) : null }

      { !loading && !error && filteredReleaseNotes.length > 0 ? (
        <div className="whats-new__timeline">
          <div className="whats-new__line" aria-hidden="true" />
          <div className="whats-new__list">
            { filteredReleaseNotes.map((release) => (
              <ReleaseCard
                key={ release.id || release.version }
                release={ release }
              />
            )) }
          </div>
        </div>
      ) : null }
    </main>
  );
};

export default WhatsNewPage;
